import React, { useState, useCallback, useEffect } from 'react'
import { InspectorMode } from './InspectorMode'
import { Eye, EyeOff, Send, Target, Layers, X } from 'lucide-react'

interface ComponentInfo {
  name: string
  path: string
  rect: DOMRect
}

interface ShellContext {
  shellId: number
  element: ComponentInfo | null
  timestamp: number
}

interface ShellUIContextProps {
  shellId: number  // Which shell this is (1 = director, 2-4 = workers)
  onContextBroadcast?: (context: ShellContext) => void
  embedded?: boolean  // If true, render inline instead of floating
}

const ORCHESTRATOR_API = window.location.hostname === 'localhost' ? 'http://localhost:3849' : '/api/orchestrator'

export const ShellUIContext: React.FC<ShellUIContextProps> = ({
  shellId,
  onContextBroadcast,
  embedded = false
}) => {
  const [inspectorEnabled, setInspectorEnabled] = useState(false)
  const [selectedElement, setSelectedElement] = useState<ComponentInfo | null>(null)
  const [allShellContexts, setAllShellContexts] = useState<Record<number, ShellContext>>({})
  const [broadcasting, setBroadcasting] = useState(false)

  const isDirector = shellId === 1

  // Poll for shell contexts from other shells (director sees all)
  useEffect(() => {
    if (!isDirector) return

    const pollContexts = async () => {
      try {
        const res = await fetch(`${ORCHESTRATOR_API}/shell-contexts`)
        if (res.ok) {
          const data = await res.json()
          setAllShellContexts(data.contexts || {})
        }
      } catch (e) {
        // Silent fail - orchestrator may not support this yet
      }
    }

    const interval = setInterval(pollContexts, 2000)
    return () => clearInterval(interval)
  }, [isDirector])

  // Broadcast selected element to orchestrator
  const broadcastContext = useCallback(async (element: ComponentInfo | null) => {
    const context: ShellContext = {
      shellId,
      element,
      timestamp: Date.now()
    }

    setBroadcasting(true)
    try {
      // API expects snake_case: shell_id
      await fetch(`${ORCHESTRATOR_API}/shell-context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shell_id: shellId,
          element,
          timestamp: Date.now()
        })
      })
      onContextBroadcast?.(context)
    } catch (e) {
      console.warn('Failed to broadcast shell context:', e)
    } finally {
      setBroadcasting(false)
    }
  }, [shellId, onContextBroadcast])

  const handleElementSelect = useCallback((info: ComponentInfo) => {
    setSelectedElement(info)
    setInspectorEnabled(false)
    broadcastContext(info)
  }, [broadcastContext])

  const handleClose = useCallback(() => {
    setInspectorEnabled(false)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedElement(null)
    broadcastContext(null)
  }, [broadcastContext])

  // Generate context string for LLM
  const generateContextPrompt = useCallback(() => {
    if (!selectedElement) return ''

    return `[UI CONTEXT - Shell ${shellId}]
Selected Element: ${selectedElement.name}
DOM Path: ${selectedElement.path}
Position: ${Math.round(selectedElement.rect.x)},${Math.round(selectedElement.rect.y)} (${Math.round(selectedElement.rect.width)}x${Math.round(selectedElement.rect.height)})

This element is the current focus for modifications.`
  }, [selectedElement, shellId])

  // Copy context to clipboard
  const copyContext = useCallback(() => {
    const ctx = generateContextPrompt()
    if (ctx) {
      navigator.clipboard.writeText(ctx)
    }
  }, [generateContextPrompt])

  return (
    <>
      <InspectorMode
        enabled={inspectorEnabled}
        mode="blue"
        onSelect={handleElementSelect}
        onClose={handleClose}
      />

      {/* UI Context Panel - embedded or floating */}
      <div
        style={embedded ? {
          width: '100%',
          background: 'rgba(17, 24, 39, 0.95)',
          borderRadius: '8px',
          padding: '10px',
        } : {
          position: 'fixed',
          bottom: '140px',
          left: '16px',
          width: '280px',
          background: 'rgba(17, 24, 39, 0.95)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px',
          padding: '12px',
          zIndex: 9998,
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Target style={{ width: '16px', height: '16px', color: isDirector ? '#f59e0b' : '#3b82f6' }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>
              Shell {shellId} {isDirector ? '(Director)' : '(Worker)'}
            </span>
          </div>
          <button
            onClick={() => setInspectorEnabled(!inspectorEnabled)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              background: inspectorEnabled ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '6px',
              color: inspectorEnabled ? '#60a5fa' : '#9ca3af',
              fontSize: '11px',
              cursor: 'pointer'
            }}
          >
            {inspectorEnabled ? <EyeOff style={{ width: '12px', height: '12px' }} /> : <Eye style={{ width: '12px', height: '12px' }} />}
            {inspectorEnabled ? 'Exit' : 'Pick'}
          </button>
        </div>

        {/* Selected Element */}
        {selectedElement ? (
          <div style={{ background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#60a5fa' }}>{selectedElement.name}</div>
                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedElement.path}
                </div>
              </div>
              <button
                onClick={clearSelection}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '2px' }}
              >
                <X style={{ width: '14px', height: '14px' }} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
              <button
                onClick={copyContext}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  padding: '6px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '6px',
                  color: '#10b981',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                <Send style={{ width: '10px', height: '10px' }} />
                Copy Context
              </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px', color: '#6b7280', fontSize: '11px' }}>
            Click "Pick" to select a UI element
          </div>
        )}

        {/* Director: Show all shell contexts */}
        {isDirector && Object.keys(allShellContexts).length > 0 && (
          <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '10px', marginTop: '10px' }}>
            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Layers style={{ width: '12px', height: '12px' }} />
              Worker Contexts
            </div>
            {Object.entries(allShellContexts)
              .filter(([id]) => parseInt(id) !== shellId)
              .map(([id, ctx]) => (
                <div
                  key={id}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    padding: '8px',
                    marginBottom: '6px',
                    fontSize: '10px'
                  }}
                >
                  <div style={{ color: '#9ca3af' }}>Shell {id}</div>
                  {ctx.element ? (
                    <div style={{ color: '#60a5fa', marginTop: '2px' }}>{ctx.element.name}</div>
                  ) : (
                    <div style={{ color: '#4b5563', fontStyle: 'italic' }}>No selection</div>
                  )}
                </div>
              ))}
          </div>
        )}

        {/* Status indicator */}
        {broadcasting && (
          <div style={{ fontSize: '10px', color: '#f59e0b', textAlign: 'center', marginTop: '8px' }}>
            Broadcasting...
          </div>
        )}
      </div>
    </>
  )
}

export default ShellUIContext
